import { Button } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useState } from "react";
import { toaster } from "@/app";
import { confirmDialog } from "@/components/confirm-dialog";
import { apiClient } from "@/lib/api/client";

export type SendProposalButtonProps = {
  yardId: string;
  /** Rang 2 (travaux_n_2) works contract whose draft proposals are sent. */
  contractId: number;
  /** Number of draft (unsent) proposed versions; disables the button at 0. */
  draftCount: number;
  /** Refresh the list once the proposal has been sent to rang 1. */
  onSent?: () => void;
};

/**
 * "Envoyer la proposition" action for the rang 2 sas prestataires screen.
 * Sending is irreversible (rang 1 receives the documents in its sas),
 * so the action always confirms before posting.
 */
export function SendProposalButton({
  yardId,
  contractId,
  draftCount,
  onSent,
}: SendProposalButtonProps) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (draftCount === 0 || loading) return;
    const plural = draftCount > 1;
    const confirmed = await confirmDialog({
      title: "Envoyer la proposition",
      message: `${draftCount} document${plural ? "s" : ""} ${
        plural ? "seront transmis" : "sera transmis"
      } au contrat travaux rang 1. Cette action est irréversible.`,
      confirmLabel: "Envoyer",
    });
    if (!confirmed) return;

    setLoading(true);
    const { data, error } = await apiClient.POST(
      "/api/yards/{yardId}/proposals/send",
      { params: { path: { yardId } }, body: { contractId } },
    );
    setLoading(false);

    if (
      isNotNil(error) ||
      isNil(data) ||
      isNotNil(data.error) ||
      isNil(data.data)
    ) {
      toaster.create({
        type: "error",
        title: "Envoi impossible",
        description:
          error?.error ??
          "Impossible d'envoyer la proposition au contrat rang 1",
      });
      return;
    }

    toaster.create({
      type: "success",
      title: "Proposition envoyée",
      description: `${data.data.sentCount} document${
        data.data.sentCount > 1 ? "s" : ""
      } transmis au contrat travaux rang 1`,
    });
    onSent?.();
  };

  return (
    <Button
      onClick={handleClick}
      disabled={draftCount === 0 || loading}
      className="text-sm"
    >
      {loading ? "Envoi..." : "Envoyer la proposition"}
    </Button>
  );
}
